import React, { useEffect, useMemo, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { PARTICULAR_REQUEST_PARAMETER_LIST_GET } from '../../../../api/ApiConst';
import ApiCaller from '../../../../api/ApiCaller';
import { TABLE_ATTRIBUTE_TYPES } from '../../../../settings/constants/types';

const MergeAction = ({ formData, setFormData, selectedRequest, allPreviousSteps, currentStepId }) => {
  const [customRequestParams, setCustomRequestParams] = useState([]);
  const [availableObjects, setAvailableObjects] = useState([]);

  useEffect(() => {
    if (!selectedRequest) {
      setCustomRequestParams([]);
      return;
    }
    const apiUrl = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll('{request-id}', selectedRequest);
    ApiCaller.callPostApi(
      apiUrl,
      null,
      {},
      (response) => {
        const params = Array.isArray(response?.data) ? response.data : [];
        setCustomRequestParams(params);
      },
      () => setCustomRequestParams([])
    );
  }, [selectedRequest]);

  const stepIdNum = useMemo(() => {
    const numeric = Number(currentStepId);
    return Number.isFinite(numeric) ? numeric : null;
  }, [currentStepId]);

  const stepObjects = useMemo(() => {
    if (!Array.isArray(allPreviousSteps) || allPreviousSteps.length === 0) return [];
    const list = [];
    allPreviousSteps.forEach((step) => {
      if (stepIdNum && step.id >= stepIdNum) return;
      const action = step.actions?.[0];
      if (!action) return;
      const cfg = action.config || {};
      if (cfg.resultAs) {
        list.push({
          label: `${cfg.resultAs} (Step ${step.id})`,
          value: `step_${step.id}.${cfg.resultAs}`,
          source: `step_${step.id}`
        });
      }
      const respParams = Array.isArray(cfg.responseParameters) ? cfg.responseParameters : [];
      respParams
        .filter((p) => (p?.type || '').toUpperCase() === TABLE_ATTRIBUTE_TYPES.OBJECT)
        .forEach((p) => {
          list.push({
            label: `${p.name} (Step ${step.id})`,
            value: `step_${step.id}.${p.name}`,
            source: `step_${step.id}`
          });
        });
    });
    return list;
  }, [allPreviousSteps, stepIdNum]);

  const requestObjects = useMemo(() => {
    const list = [];
    customRequestParams
      .filter((p) => (p?.type || '').toUpperCase() === TABLE_ATTRIBUTE_TYPES.OBJECT)
      .forEach((p) => {
        list.push({
          label: `${p.name} (Custom Request)`,
          value: `custom_request.${p.name}`,
          source: 'custom_request'
        });
      });
    return list;
  }, [customRequestParams]);

  useEffect(() => {
    setAvailableObjects([...
      requestObjects,
      ...stepObjects
    ]);
  }, [requestObjects, stepObjects]);

  const selectedSourceValues = useMemo(() => {
    const values = Array.isArray(formData?.sourceObjects) ? formData.sourceObjects : [];
    return availableObjects.filter((o) => values.includes(o.value));
  }, [availableObjects, formData?.sourceObjects]);

  const handleSourcesChange = (selected) => {
    const values = Array.isArray(selected) ? selected.map((s) => s.value) : [];
    setFormData((prev) => ({ ...prev, sourceObjects: values }));
  };

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name) => (selected) => {
    setFormData((prev) => ({ ...prev, [name]: selected ? selected.value : '' }));
  };

  return (
    <div>
      <Row>
        <Col md={12} className="mb-3">
          <Form.Label>Select Object Sources</Form.Label>
          <Select
            isMulti
            options={availableObjects}
            value={selectedSourceValues}
            onChange={handleSourcesChange}
            placeholder="Select objects to merge"
            className="react-select-container"
          />
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Merge Strategy</Form.Label>
          <Select
            options={[
              { value: 'SHALLOW', label: 'Shallow' },
              { value: 'DEEP', label: 'Deep' }
            ]}
            value={formData?.mergeStrategy ? { value: formData.mergeStrategy, label: formData.mergeStrategy.charAt(0) + formData.mergeStrategy.slice(1).toLowerCase() } : null}
            onChange={handleSelectChange('mergeStrategy')}
            placeholder="Select strategy"
            className="react-select-container"
            isClearable
          />
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Conflict Resolution</Form.Label>
          <Select
            options={[
              { value: 'LAST_WINS', label: 'Last Wins' },
              { value: 'FIRST_WINS', label: 'First Wins' }
            ]}
            value={formData?.conflictResolution ? { value: formData.conflictResolution, label: formData.conflictResolution.replace('_', ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) } : null}
            onChange={handleSelectChange('conflictResolution')}
            placeholder="Select resolution"
            className="react-select-container"
            isClearable
          />
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Include Keys (comma separated)</Form.Label>
          <Form.Control
            name="includeKeys"
            value={formData?.includeKeys || ''}
            onChange={handleBasicChange}
            placeholder="e.g. name,email,address"
          />
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>Exclude Keys (comma separated)</Form.Label>
          <Form.Control
            name="excludeKeys"
            value={formData?.excludeKeys || ''}
            onChange={handleBasicChange}
            placeholder="e.g. password,token"
          />
        </Col>

        <Col md={12} className="mb-3">
          <Form.Label>Result Variable Name</Form.Label>
          <Form.Control
            name="resultAs"
            value={formData?.resultAs || ''}
            onChange={handleBasicChange}
            placeholder="Enter result variable name"
          />
        </Col>
      </Row>
    </div>
  );
};

export default MergeAction;


